import React, { useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { getCountryCoord } from '../../lib/countryCoords';

const fmtEuro = (n) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);

export const ModelsSalesMap = ({ rows }) => {
    const { bubbles, unlocated } = useMemo(() => {
        const byCountry = {};
        let unknown = 0;
        rows.forEach(r => {
            const coord = getCountryCoord(r.country);
            if (!coord) { unknown += r.count; return; }
            const label = coord.label || r.country;
            if (!byCountry[label]) byCountry[label] = { label, coord, count: 0, value: 0, models: {} };
            byCountry[label].count += r.count;
            byCountry[label].value += r.value;
            byCountry[label].models[r.modello] = (byCountry[label].models[r.modello] || 0) + r.count;
        });
        const list = Object.values(byCountry).map(b => ({
            ...b,
            topModels: Object.entries(b.models).sort((a, c) => c[1] - a[1]).slice(0, 6),
        })).sort((a, b) => b.count - a.count);
        return { bubbles: list, unlocated: unknown };
    }, [rows]);

    const max = bubbles[0]?.count || 1;

    return (
        <div className="space-y-2" data-testid="models-sales-map">
            <div className="rounded-sm overflow-hidden border" style={{ height: 420 }}>
                <MapContainer center={[48, 12]} zoom={3} minZoom={2} scrollWheelZoom={false} style={{ height: '100%', width: '100%' }}>
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {bubbles.map(b => (
                        <CircleMarker
                            key={b.label}
                            center={[b.coord.lat, b.coord.lng]}
                            radius={Math.max(9, Math.min(42, 8 + Math.sqrt(b.count / max) * 34))}
                            pathOptions={{ color: '#ffffff', weight: 2, fillColor: '#D32F2F', fillOpacity: 0.75 }}
                        >
                            <Tooltip direction="top" offset={[0, -6]} opacity={0.95}>
                                <span className="font-medium">{b.label}</span> · {b.count} veicoli
                            </Tooltip>
                            <Popup>
                                <div className="min-w-[220px] space-y-1" data-testid={`models-map-popup-${b.label}`}>
                                    <div className="font-heading uppercase text-sm">{b.label}</div>
                                    <div className="text-xs text-muted-foreground">{b.count} veicoli · {fmtEuro(b.value)}</div>
                                    <ul className="text-xs mt-1 space-y-0.5">
                                        {b.topModels.map(([m, n]) => (
                                            <li key={m} className="flex justify-between gap-3">
                                                <span className="truncate">{m}</span><b>{n}</b>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </Popup>
                        </CircleMarker>
                    ))}
                </MapContainer>
            </div>
            <p className="text-[11px] text-muted-foreground">
                Dimensione della bolla proporzionale ai veicoli venduti nel paese. Clicca una bolla per i modelli più venduti.
                {unlocated > 0 && ` · ${unlocated} veicoli con nazione non riconosciuta non mostrati.`}
            </p>
        </div>
    );
};

export default ModelsSalesMap;
